'use client'
import { lightTheme } from '@/config/theme'
import { usePDFDrawer } from '@/contexts/PDFDrawerContext'
import { BubbleProps } from 'flowise-embed'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'

const BubbleChat = dynamic(() => import('flowise-embed-react').then(mod => mod.BubbleChat), {
  ssr: false,
})

const chatflowid = process.env.NEXT_PUBLIC_CHATFLOW_ID ?? '1a6f3a3e-6e7b-47e2-98ed-df7c0aa60e22'
// Usa il proxy locale per bypassare CORS invece dell'URL diretto
const apiHost =
  typeof window !== 'undefined'
    ? `${window.location.origin}/api/flowise`
    : (process.env.NEXT_PUBLIC_CHAT_SERVER_URL ?? 'https://flowise.polisnet.mywire.org')

const ENABLE_MESSAGES_OBSERVER = false
export const ChatBotFlowise = () => {
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const [language, setLanguage] = useState('it')
  const { openPDFDrawer } = usePDFDrawer()

  useEffect(() => {
    const lang = localStorage.getItem('language') || 'it'
    setLanguage(lang)
  }, [])

  useEffect(() => {
    console.log('ChatBotFlowise', { chatflowid, apiHost, language })
  }, [language])

  // Aggiunge listener per intercettare i click sui link PDF nello Shadow DOM
  useEffect(() => {
    if (ENABLE_MESSAGES_OBSERVER) {
      const setupShadowDOMListener = () => {
        // Trova il componente flowise-chatbot
        const chatbotElement = document.querySelector('flowise-chatbot')
        if (!chatbotElement) {
          console.log('Flowise chatbot element not found, retrying...')
          setTimeout(setupShadowDOMListener, 1000)
          return
        }

        // Accedi allo Shadow DOM
        const shadowRoot = chatbotElement.shadowRoot
        if (!shadowRoot) {
          console.log('Shadow DOM not accessible, retrying...')
          setTimeout(setupShadowDOMListener, 1000)
          return
        }

        console.log('✅ Shadow DOM accessed successfully')

        // Listener per intercettare i click sui link PDF nello Shadow DOM
        const handleShadowClick = (event: Event) => {
          console.log('🚀 ~ handleShadowClick ~ event:', event)
          const target = event.target as HTMLElement
          const link = target.closest('a')

          if (!link) return

          const href = link.getAttribute('href')
          if (!href) return

          // Verifica se è un link della guida dello studente
          if (href.includes('guida-dello-studente')) {
            event.preventDefault()
            event.stopPropagation()
            event.stopImmediatePropagation()

            console.log('Guide link intercepted in Shadow DOM:', href)

            try {
              const urlObj = new URL(href)
              const pageParam =
                urlObj.searchParams.get('page') || urlObj.searchParams.get('pagina') || '1'
              const language = urlObj.searchParams.get('language') || 'it'

              const documentName =
                language === 'it'
                  ? 'Guida-dello-Studente-2025_2026.pdf'
                  : 'Guida-dello-Studente-2025_2026_en.pdf'
              // Apri il drawer PDF con la pagina specificata
              openPDFDrawer(pageParam, documentName, language)

              console.log('Guide opened:', { original: href, page: pageParam })
            } catch (error) {
              console.warn('Errore nel processare il link guida:', error)

              openPDFDrawer('1', 'Guida-dello-Studente-2025_2026.pdf', 'it')
            }
          }
        }

        // Aggiungi il listener allo Shadow DOM
        shadowRoot.addEventListener('click', handleShadowClick, true)

        // Observer per monitorare i cambiamenti nello Shadow DOM
        const observer = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach(node => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  const element = node as Element
                  // Verifica se sono stati aggiunti nuovi link della guida dello studente
                  const allLinks = element.querySelectorAll('a')
                  const guideLinks = Array.from(allLinks).filter(link =>
                    link.getAttribute('href')?.includes('guida-dello-studente')
                  )
                  if (guideLinks.length > 0) {
                    console.log('New guide links detected in Shadow DOM:', guideLinks.length)
                    // Processa immediatamente i nuovi link
                  }
                }
              })
            }
          })
        })

        observer.observe(shadowRoot, {
          childList: true,
          subtree: true,
        })

        return () => {
          shadowRoot.removeEventListener('click', handleShadowClick, true)
          observer.disconnect()
        }
      }

      // Avvia il setup con un piccolo delay per assicurarsi che il chatbot sia renderizzato
      const timeoutId = setTimeout(setupShadowDOMListener, 2000)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  if (!chatflowid || !apiHost) {
    return <div>ChatBotFlowise non configurato</div>
  }

  const botProps: BubbleProps = {
    chatflowid,
    apiHost,
    sourceDocsTitle: 'Documenti',
    chatflowConfig: {
      // topK: 2
    },
    /* observersConfig: {
      // (optional) Allows you to execute code in parent based upon signal observations within the chatbot.
      // The userinput field submitted to bot ("" when reset by bot)
      observeUserInput: userInput => {
        console.log('observeUserInput', { userInput })
      },
      // The bot message stack has changed
      observeMessages: messages => {
        console.log('observeMessages', { messages })
      },
      // The bot loading signal changed
      observeLoading: loading => {
        console.log({ loading })
      },
    },*/
    theme: {
      button: {
        backgroundColor: lightTheme.primary,
        iconColor: '#ffffff',
        // autoWindowOpen: {
        //   autoOpen: true, //parameter to control automatic window opening
        //   openDelay: 2, // Optional parameter for delay time in seconds
        //   autoOpenOnMobile: false, //parameter to control automatic window opening in mobile
        // },
      },
      tooltip: {
        showTooltip: true,
        tooltipMessage: 'Ciao! Sono SophIA, il tuo assistente virtuale. chiedimi pure!',
        tooltipBackgroundColor: lightTheme.primary,
        tooltipTextColor: 'white',
        tooltipFontSize: 16,
      },
      disclaimer: {
        title: 'Disclaimer',
        message:
          'POSSIAMO ANCHE TOGLIERLO: Utilizzando questo chatbot, accetti i Termini e Condizioni..bla bla.. </a>',
        textColor: lightTheme.foreground,
        buttonColor: lightTheme.primary,
        buttonText: 'Inizia Chat',
        buttonTextColor: lightTheme.primaryForeground,
        blurredBackgroundColor: 'rgba(0, 0, 0, 0.4)', //The color of the blurred background that overlays the chat interface
        backgroundColor: lightTheme.background,
        denyButtonText: 'Annulla',
        denyButtonBgColor: lightTheme.destructive,
      },
      // form: {
      //   backgroundColor: lightTheme.primary,
      //   textColor: lightTheme.foreground,
      // },
      chatWindow: {
        showTitle: true,
        title: 'SophIA',
        backgroundColor: lightTheme.background,
        titleBackgroundColor: lightTheme.primary,

        titleAvatarSrc: '/images/logotipo-tor-vergata-white.svg',

        showAgentMessages: false,
        welcomeMessage: `Ciao! Sono SophIA, il tuo assistente virtuale. Posso aiutarti con qualsiasi domanda o problema.`,
        errorMessage: 'Si è verificato un errore. Per favore, riprova.',

        height: 900,
        width: typeof window !== 'undefined' ? window?.innerWidth * 0.5 : 500,
        fontSize: 16,
        renderHTML: true,
        //clearChatOnReload: true,

        poweredByTextColor: '#303235',
        botMessage: {
          backgroundColor: '#f7f8ff',
          textColor: lightTheme.primary,
          showAvatar: true,
          avatarSrc: '/images/logotipo-tor-vergata-green.svg',
        },
        userMessage: {
          backgroundColor: lightTheme.primary,
          textColor: '#ffffff',
          //showAvatar: true,
          // avatarSrc:          'https://raw.githubusercontent.com/zahidkhawaja/langchain-chat-nextjs/main/public/usericon.png',
        },

        textInput: {
          placeholder: 'Chiedimi qualunque cosa!',
          backgroundColor: '#ffffff',
          textColor: '#303235',
          sendButtonColor: lightTheme.primary,
          maxChars: 200,
          maxCharsWarningMessage:
            'Hai superato il limite di caratteri. Per favore, inserisci meno di 200 caratteri.',
          autoFocus: true,
          sendMessageSound: true,
          receiveMessageSound: true,
        },

        feedback: {
          color: '#303235',
        },
        dateTimeToggle: {
          date: true,
          time: true,
        },
        footer: {
          textColor: '#303235',
          text: 'Powered by',
          company: 'Polis-Net',
          companyLink: 'https://polis-net.it',
        },
      },
    },
  }

  return (
    <div ref={chatContainerRef}>
      <BubbleChat {...botProps} />
    </div>
  )
}

export default ChatBotFlowise
// Define the props type including the theme
